import { Box, Button, Container, Grid, Typography, Stack } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const features = [
  {
    icon: <ManageAccountsIcon sx={{ fontSize: 28 }} />,
    iconBg: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
    iconGlow: "rgba(79,110,247,0.3)",
    eyebrow: "School Operations",
    title: "Intelligent School Operations",
    bullets: [
      "Automate attendance, fees & timetables",
      "Real-time dashboards & smart notifications",
      "Full operational visibility in one place",
      "Reduce errors, save admin time by 70%",
    ],
    imageKey: "right",
    imageSrc: "/feature1.png",
    imageAlt: "Smart Administration",
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 28 }} />,
    iconBg: "linear-gradient(135deg, #0EA5E9 0%, #4F6EF7 100%)",
    iconGlow: "rgba(14,165,233,0.3)",
    eyebrow: "AI Learning",
    title: "AI That Learns With Students",
    bullets: [
      "Personalized 24/7 academic support",
      "Intelligent insights on weak areas",
      "Structured explanations for better clarity",
      "Measurable academic growth, step by step",
    ],
    imageKey: "left",
    imageSrc: "/feature2.png",
    imageAlt: "AI Tutor",
  },
  {
    icon: <SportsEsportsIcon sx={{ fontSize: 28 }} />,
    iconBg: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
    iconGlow: "rgba(16,185,129,0.3)",
    eyebrow: "Gamified Learning",
    title: "Gamified Engagement Engine",
    bullets: [
      "Skill-based quizzes & achievements",
      "Real-time performance analytics",
      "Track learning gaps automatically",
      "Make learning dynamic & enjoyable",
    ],
    imageKey: "right",
    imageSrc: "/feature3.png",
    imageAlt: "Gamified Learning",
  },
];

const FeatureBlock = ({ feature, index }) => {
  const isReversed = feature.imageKey === "left";

  return (
    <Grid
      container
      spacing={{ xs: 6, md: 10 }}
      alignItems="center"
      mb={{ xs: 12, md: 16 }}
      flexDirection={{ xs: "column", md: isReversed ? "row-reverse" : "row" }}
    >
      {/* Text side */}
      <Grid item xs={12} md={6}>
        {/* Icon + eyebrow */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: feature.iconBg,
              color: "#fff",
              flexShrink: 0,
              boxShadow: `0 8px 20px ${feature.iconGlow}`,
            }}
          >
            {feature.icon}
          </Box>
          <Typography
            fontSize="0.78rem"
            fontWeight={700}
            letterSpacing="0.08em"
            sx={{
              background: feature.iconBg,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textTransform: "uppercase",
            }}
          >
            {feature.eyebrow}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          variant="h3"
          fontWeight={800}
          color="#0D1B4B"
          gutterBottom
          sx={{ lineHeight: 1.2, mb: 3 }}
        >
          {feature.title}
        </Typography>

        {/* Bullet list */}
        <Stack spacing={1.5} mb={4}>
          {feature.bullets.map((b, i) => (
            <Box key={i} display="flex" alignItems="flex-start" gap={1.5}>
              <CheckCircleIcon
                sx={{
                  fontSize: 20,
                  mt: 0.15,
                  flexShrink: 0,
                  background: feature.iconBg,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              />
              <Typography variant="body1" color="#475569" lineHeight={1.65}>
                {b}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Grid>

      {/* Image side */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {/* Glow */}
          <Box
            sx={{
              position: "absolute",
              inset: "10%",
              borderRadius: "50%",
              background: feature.iconBg,
              opacity: 0.08,
              filter: "blur(50px)",
            }}
          />
          <Box
            component="img"
            src={feature.imageSrc}
            alt={feature.imageAlt}
            sx={{
              width: "100%",
              maxWidth: 500,
              borderRadius: "24px",
              boxShadow: `0 40px 100px rgba(13,27,75,0.12)`,
              border: "1px solid rgba(255,255,255,0.8)",
              position: "relative",
              zIndex: 1,
              transition: "transform 0.4s ease",
              "&:hover": { transform: "scale(1.02)" },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const StreamLine = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        background: "#F8FAFF",
      }}
    >
      <Container maxWidth="lg">
        {/* INTRO SECTION */}
        <Box textAlign="center" mb={{ xs: 10, md: 14 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.8,
              borderRadius: "100px",
              bgcolor: "rgba(79,110,247,0.08)",
              border: "1px solid rgba(79,110,247,0.2)",
              mb: 3,
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
              FEATURES
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 900,
              color: "#0D1B4B",
              mb: 2.5,
            }}
          >
            Streamline Your Workflow with{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              MySchoolling
            </Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "#64748B",
              maxWidth: 760,
              mx: "auto",
              lineHeight: 1.8,
              fontWeight: 400,
            }}
          >
            An AI-powered digital ecosystem designed to automate school operations,
            strengthen parent engagement, and empower students with intelligent
            learning tools — built for modern education.
          </Typography>
        </Box>

        {/* WHY CHOOSE Header */}
        <Box id="Why_Choose" textAlign="center" mb={{ xs: 8, md: 10 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.8rem", md: "2.6rem" },
              fontWeight: 900,
              color: "#0D1B4B",
            }}
          >
            Why Choose MySchoolling?
          </Typography>
          <Box
            sx={{
              width: 60,
              height: 4,
              borderRadius: 2,
              background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
              mx: "auto",
              mt: 2,
            }}
          />
        </Box>

        {/* Feature blocks */}
        {features.map((feature, index) => (
          <FeatureBlock key={index} feature={feature} index={index} />
        ))}

        {/* CTA BANNER */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "28px",
            p: { xs: 6, md: 10 },
            textAlign: "center",
            background: "linear-gradient(135deg, #0D1B4B 0%, #1A2F6E 50%, #4F6EF7 100%)",
            backgroundSize: "200% 200%",
            animation: "gradient-shift 8s ease infinite",
            "@keyframes gradient-shift": {
              "0%":   { backgroundPosition: "0% 50%" },
              "50%":  { backgroundPosition: "100% 50%" },
              "100%": { backgroundPosition: "0% 50%" },
            },
          }}
        >
          {/* Decorative orb */}
          <Box
            sx={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              left: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h2"
              fontWeight={900}
              color="#fff"
              gutterBottom
              sx={{ fontSize: { xs: "1.8rem", md: "2.8rem" }, mb: 2 }}
            >
              Ready to Transform Your School?
            </Typography>

            <Typography
              variant="h6"
              sx={{
                maxWidth: 640,
                mx: "auto",
                color: "rgba(255,255,255,0.75)",
                lineHeight: 1.8,
                fontWeight: 400,
                mb: 5,
              }}
            >
              Join forward-thinking institutions already leveraging AI-powered
              automation and intelligent learning tools to stay ahead.
            </Typography>

            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/contact")}
              sx={{
                background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                color: "#fff",
                fontWeight: 700,
                px: 5,
                py: 1.8,
                borderRadius: "50px",
                fontSize: "1rem",
                boxShadow: "0 16px 48px rgba(79,110,247,0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3A5AE8 0%, #6D28D9 100%)",
                  boxShadow: "0 20px 60px rgba(79,110,247,0.55)",
                  transform: "translateY(-3px)",
                },
              }}
            >
              Book a Demo
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default StreamLine;
